import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import type { AxiosRequestConfig } from 'axios';
import { useApiClient } from '../ApiClientProvider.js';

/**
 * Typed GET query backed by the shared axios instance from ApiClientProvider.
 *
 * Usage (once openapi-typescript types are generated):
 *   type Certs = paths["/certificates"]["get"]["responses"][200]["content"]["application/json"]
 *   const { data } = useApiQuery<Certs>(["certificates", params], "/certificates", { params })
 */
export function useApiQuery<TData>(
  queryKey: unknown[],
  path: string,
  config?: AxiosRequestConfig,
  options?: Omit<UseQueryOptions<TData, Error>, 'queryKey' | 'queryFn'>,
) {
  const client = useApiClient();
  return useQuery<TData, Error>({
    queryKey,
    queryFn: () => client.get<TData>(path, config).then((r) => r.data),
    ...options,
  });
}

/**
 * Typed mutation backed by the shared axios instance from ApiClientProvider.
 *
 * Usage:
 *   const { mutate } = useApiMutation<ResponseType, RequestBody>("/certificates", "post")
 */
export function useApiMutation<TData, TVariables = void>(
  path: string,
  method: 'delete' | 'patch' | 'post' | 'put' = 'post',
  options?: UseMutationOptions<TData, Error, TVariables>,
) {
  const client = useApiClient();
  return useMutation<TData, Error, TVariables>({
    mutationFn: (variables) => client[method]<TData>(path, variables).then((r) => r.data),
    ...options,
  });
}
